import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import React from 'react'
import BmAvatar from './BmAvatar'
import { FBObjectTypeString } from './facebook-object-selector'

interface FbAsset {
  id: string
  name: string
  businessId?: string
  businessName?: string
  businessPictureUri?: string
}

interface FbFieldChangeDialogProps {
  open: boolean
  field: FBObjectTypeString
  currentAsset?: FbAsset
  candidateAsset: FbAsset
  confirming: boolean
  error?: string
  onConfirm: () => void
  onCancel: () => void
}

const FIELD_LABELS: Record<FBObjectTypeString, string> = {
  ad_account: 'Ad Account',
  page: 'Page',
  pixel: 'Dataset',
  offline: 'Offline Dataset',
  ig: 'Instagram Account',
}

// Per-field warning copy shown before a change is confirmed. Ad Account and Page carry a real
// data-continuity warning (historical data does not follow/merge); the rest are lower-stakes
// confirmations with no equivalent "old data" concept.
const warningCopyFor = (
  field: FBObjectTypeString,
  current: FbAsset | undefined,
  candidate: FbAsset
) => {
  switch (field) {
    case 'ad_account':
      return (
        <>
          Switching Ad Accounts means new ad performance data will start coming in from{' '}
          <strong>{candidate.name}</strong>.{' '}
          {current && (
            <>
              Historical data already attributed to <strong>{current.name}</strong> is retained but
              will <strong>not</strong> be merged into the new account.
            </>
          )}{' '}
          Your Instagram Account and Dataset(s) are tied to the Ad Account they were selected under
          — after this switch, check them below and reselect any that no longer show as connected.
        </>
      )
    case 'page':
      return (
        <>
          Switching Pages means new leads will start coming in from{' '}
          <strong>{candidate.name}</strong>.{' '}
          {current && (
            <>
              Historical leads already attributed to <strong>{current.name}</strong> are retained
              but will <strong>not</strong> be merged into the new page.
            </>
          )}
        </>
      )
    case 'pixel':
    case 'offline':
      return (
        <>
          Using the same dataset for both online and offline data is fine. Switching datasets does
          not merge historical data between them.
        </>
      )
    case 'ig':
      return (
        <>
          You are switching from{' '}
          {current ? <strong>{current.name}</strong> : 'no Instagram account'} to{' '}
          <strong>{candidate.name}</strong>.
        </>
      )
  }
}

const AssetCard = ({ asset, label }: { asset?: FbAsset; label: string }) => (
  <div className="border rounded p-3" style={{ flex: 1, minWidth: 0 }}>
    <Typography variant="overline" color="textSecondary" style={{ lineHeight: 1.2 }}>
      {label}
    </Typography>
    {asset ? (
      <>
        <Typography variant="subtitle1" style={{ fontWeight: 600, lineHeight: 1.3 }}>
          {asset.name}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          id: {asset.id}
        </Typography>
        {(asset.businessId || asset.businessName || asset.businessPictureUri) && (
          <div className="d-flex align-items-start mt-2 pt-2 border-top" style={{ gap: 8 }}>
            <BmAvatar name={asset.businessName} pictureUri={asset.businessPictureUri} size={22} />
            <div style={{ minWidth: 0 }}>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {asset.businessName ?? 'Unknown Business Manager'}
              </Typography>
              {asset.businessId && (
                <Typography variant="caption" color="textSecondary">
                  id: {asset.businessId}
                </Typography>
              )}
            </div>
          </div>
        )}
      </>
    ) : (
      <Typography variant="body2" color="textSecondary">
        Not yet assigned
      </Typography>
    )}
  </div>
)

const FbFieldChangeDialog = (props: FbFieldChangeDialogProps) => {
  const { open, field, currentAsset, candidateAsset, confirming, error, onConfirm, onCancel } =
    props

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>Change {FIELD_LABELS[field]}?</DialogTitle>
      <DialogContent>
        <div className="d-flex align-items-stretch mb-4" style={{ gap: 16 }}>
          <AssetCard asset={currentAsset} label="Current" />
          <div className="d-flex align-items-center" style={{ fontSize: 24, color: '#9e9e9e' }}>
            &rarr;
          </div>
          <AssetCard asset={candidateAsset} label="New" />
        </div>
        <p>{warningCopyFor(field, currentAsset, candidateAsset)}</p>
        {error && (
          <Alert severity="error" className="mt-2">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={confirming}>
          Cancel
        </Button>
        <Button onClick={onConfirm} color="primary" variant="contained" disabled={confirming}>
          {confirming ? 'Verifying...' : 'Confirm & Verify'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FbFieldChangeDialog
